/**
 * Contract-readback presentation for the customer-360 credit block
 * (router#2061).
 *
 * The displayed line is `creditCapUsdMicros(overview.layers)` — the same
 * long-term + temporary sum the gateway spends against. A missing grant
 * is prepaid, not "inherit the platform default limit".
 */

import { creditCapUsdMicros, type CreditLayerUsage } from '@nadoutong/contracts';

export type CreditContractReadback =
  | { readonly kind: 'prepaid' }
  | {
      readonly kind: 'granted';
      readonly limitUsdMicros: number;
      readonly paymentTermsDays: number;
      readonly paymentTermsInherited: boolean;
    };

export function resolveCreditContractReadback(input: {
  readonly layers: Pick<CreditLayerUsage, 'long_term_cap_usd_micros' | 'temporary_cap_usd_micros'>;
  readonly billingGraceDays: number | null;
  readonly effectiveGraceDays: number;
}): CreditContractReadback {
  const cap = creditCapUsdMicros(input.layers);
  if (cap <= 0) {
    return { kind: 'prepaid' };
  }
  return {
    kind: 'granted',
    limitUsdMicros: cap,
    paymentTermsDays: input.effectiveGraceDays,
    paymentTermsInherited: input.billingGraceDays === null,
  };
}

/**
 * CNY side of the combined-cap card. The USD figure is long-term +
 * temporary; the signed CNY originals must sum the same way. A missing
 * archive on any active layer (pre-cutover grant) returns null so the
 * caller can convert the cap at the live listing rate instead of
 * showing a partial signed total against the full cap.
 */
export function archivedCapCnyCents(profile: {
  readonly credit_limit_usd_micros: number | null;
  readonly credit_limit_cny_cents?: number | null;
  readonly temporary_limit_usd_micros?: number | null;
  readonly temporary_limit_cny_cents?: number | null;
}): number | null {
  const hasLong = (profile.credit_limit_usd_micros ?? 0) > 0;
  const hasTemp = (profile.temporary_limit_usd_micros ?? 0) > 0;
  if (!hasLong && !hasTemp) return null;
  const longPart = hasLong ? profile.credit_limit_cny_cents : 0;
  const tempPart = hasTemp ? profile.temporary_limit_cny_cents : 0;
  if (longPart === null || longPart === undefined) return null;
  if (tempPart === null || tempPart === undefined) return null;
  return longPart + tempPart;
}
